import { Action } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash'

import {
  App,
  DataSourceMapping,
  ExecutionStepStatus,
  Feature,
  FeatureStatus,
  InitialApp,
  NoTrigger,
  TriggerTypes,
  Workflow
} from '@/pages/SpaceAppBuilder/utilities/interfaces'

import * as actionTypes from './actionTypes'
import { assignStepCount, transformVariableData } from './utils'

interface FetchAppInstancesResponse extends Action {
  response: App
}

interface FeatureResponse extends Action {
  response: Feature
}

interface FeatureExecutionResponse extends Action {
  response: ExecutionId
}

interface ExecutionId {
  id?: string
  featureExecutionId?: string
}

interface SpaceAppBuilderFeaturesPageState {
  app: App | InitialApp
  feature: Feature | undefined
  execution: ExecutionId | undefined
  featureExecution: Execution | StepFunctionExecution | undefined
  isSaving: boolean
  isError: boolean
  dataSourcesMapping: Record<string | number, DataSourceMapping[]> | undefined
  notificationSources: NotificationSource[]
  buildError: any
}

interface FetchDataSourceDocsResponse extends Action {
  response: DataSourceMapping[]
  additionalActionParams: any
}

interface ExecutionResponse extends Action {
  response: Execution | any
}

interface Execution extends Action {
  elementInstanceId: number
  formulaInstanceId: number
  id: number
  status: string
  stepExecutions: []
}

interface StepFunctionExecution extends Action {
  id: number
  status: string
  stepExecutions: any[]
}

const initialState: SpaceAppBuilderFeaturesPageState = {
  app: {
    name: '',
    features: [],
    dataSources: []
  },
  execution: undefined,
  featureExecution: undefined,
  feature: {
    name: '',
    workflow: {
      trigger: { type: 'none' } as NoTrigger,
      steps: []
    },
    status: FeatureStatus.DRAFT
  },
  dataSourcesMapping: {},
  notificationSources: [],
  isSaving: false,
  isError: false,
  buildError: null
}

interface FetchNotificationSourcesResponse extends Action {
  response: NotificationSource[]
}

interface NotificationSource {
  connectorInstanceId: string
  iconUrl: string
  id: string
  isDefault: boolean
  source: string
  spaceId: string
  title: string
}

const transformWorkflowData = (workflow: Workflow | undefined) => {
  const workflowClone = cloneDeep(workflow)
  if (workflowClone) {
    workflowClone.steps = assignStepCount(workflowClone.steps)
    workflowClone.steps = transformVariableData(workflowClone.steps, true)
  }
  return workflowClone
}

export default function spaceAppBuilderFeaturesPageReducer(
  state = initialState,
  action: Action
): SpaceAppBuilderFeaturesPageState {
  switch (action.type) {
    case actionTypes.CREATE_APP_BUILDER_APP_INSTANCE:
    case actionTypes.FETCH_APP_BUILDER_APP_INSTANCE_SUCCESS: {
      const app = (action as FetchAppInstancesResponse).response
      return {
        ...state,
        app
      }
    }
    case actionTypes.RESET_APP_BUILDER_APP_INSTANCE: {
      return {
        ...state,
        app: initialState.app
      }
    }
    case actionTypes.CREATE_APP_BUILDER_FEATURE:
    case actionTypes.FETCH_APP_BUILDER_FEATURE_SUCCESS:
    case actionTypes.FETCH_APP_BUILDER_FEATURE_NO_LOADER_SUCCESS: {
      const feature = (action as FeatureResponse).response

      const featureClone = cloneDeep(feature)
      featureClone.workflow = transformWorkflowData(feature.workflow)
      return {
        ...state,
        feature: featureClone
      }
    }
    case actionTypes.RESET_EXECUTE_APP_BUILDER_FEATURE: {
      return {
        ...state,
        execution: undefined,
        featureExecution: initialState.featureExecution,
        buildError: initialState.buildError
      }
    }
    case actionTypes.EXECUTE_APP_BUILDER_FEATURE_SUCCESS: {
      const response = (action as FeatureExecutionResponse).response
      const execution = {
        id: response.featureExecutionId || response.id
      }
      return {
        ...state,
        execution
      }
    }
    case actionTypes.CREATE_APP_BUILDER_FEATURE_SUCCESS: {
      const feature = (action as FeatureResponse).response
      const updatedApp = cloneDeep(state.app)
      if (!updatedApp.features) {
        updatedApp.features = []
      }
      updatedApp.features.push(feature)
      return {
        ...state,
        app: updatedApp
      }
    }
    case actionTypes.UPDATE_APP_BUILDER_FEATURE_SUCCESS: {
      const feature = (action as FeatureResponse).response
      const updatedApp = cloneDeep(state.app)
      const updatedFeatureIndex = updatedApp.features?.findIndex((feat: Feature) => {
        return feat.id === feature.id
      })
      if (updatedFeatureIndex && updatedApp?.features) {
        delete feature.workflow
        updatedApp.features[updatedFeatureIndex] = feature
      }

      return {
        ...state,
        app: updatedApp
      }
    }
    case actionTypes.UPDATE_FEATURE_WORKFLOW: {
      const workflow = (action as Feature).workflow
      const featureId = (action as Feature).id
      return {
        ...state,
        // @ts-expect-error
        feature: {
          ...state.feature,
          workflow: state.feature?.id === featureId ? transformWorkflowData(workflow) : state.feature?.workflow
        }
      }
    }
    case actionTypes.UPDATE_FEATURE_STATUS: {
      const status = (action as Feature).status
      const featureId = (action as Feature).id
      const updatedApp = cloneDeep(state.app)
      const updatedFeatureIndex = updatedApp.features?.findIndex((feat: Feature) => {
        return feat.id === featureId
      })
      if (updatedFeatureIndex && updatedApp?.features) {
        updatedApp.features[updatedFeatureIndex].status = status
      }
      return {
        ...state,
        app: updatedApp,
        // @ts-expect-error
        feature: {
          ...state.feature,
          status
        }
      }
    }
    case actionTypes.PATCH_APP_BUILDER_FEATURE_SUCCESS: {
      const feature = (action as FeatureResponse).response
      const updatedApp = cloneDeep(state.app)
      const updatedFeatureIndex = updatedApp.features?.findIndex((feat: Feature) => {
        return feat.id === feature.id
      })
      if (updatedFeatureIndex && updatedApp?.features) {
        updatedApp.features[updatedFeatureIndex] = feature
      }
      return {
        ...state,
        app: updatedApp,
        feature: {
          ...feature,
          workflow: state.feature?.workflow
        }
      }
    }
    case actionTypes.FETCH_APP_BUILDER_FEATURE_EXECUTION_SUCCESS: {
      const response = (action as ExecutionResponse).response

      const triggerType = state.feature?.workflow?.trigger.type

      let newFeatureExecution
      if (triggerType === TriggerTypes.microappView) {
        newFeatureExecution = {
          id: response.id,
          status: response.status || ExecutionStepStatus.Pending, // Default to pending
          stepExecutions: response.stepExecutions
        }
      } else {
        newFeatureExecution = response
      }
      return {
        ...state,
        featureExecution: {
          ...state.featureExecution,
          ...newFeatureExecution
        }
      }
    }
    case actionTypes.FETCH_APP_BUILDER_DATASOURCE_DOCS_SUCCESS: {
      const { dataSourceKey, isPaginatedEnabled } = (action as FetchDataSourceDocsResponse).additionalActionParams
      let dataSourceMapping = (action as FetchDataSourceDocsResponse).response
      if (isPaginatedEnabled) {
        dataSourceMapping = dataSourceMapping.map((dataSourceItem: DataSourceMapping) => {
          // Pagination is true if we have "limit" or "nextPage"
          let isPaginated = false
          const result: DataSourceMapping = {
            ...dataSourceItem,
            parameters: dataSourceItem.parameters.reduce((acc: any, param: any) => {
              let item = { ...param }
              if (param.name === 'nextPage') {
                isPaginated = true
                // Hijack nextPage and turn it into limit
                item = {
                  description: 'Limit the total number of returned items',
                  in: 'query',
                  required: false,
                  type: 'integer',
                  name: 'limit'
                }
              } else if (param.name === 'pageSize') {
                // Remove pageSize
                return acc
              }

              acc.push(item)
              return acc
            }, [])
          }

          // Add the isPaginated flag
          if (isPaginated) {
            result.isPaginated = isPaginated
          }

          return result
        })
      }

      return {
        ...state,
        dataSourcesMapping: {
          ...state.dataSourcesMapping,
          [dataSourceKey]: dataSourceMapping
        }
      }
    }
    case actionTypes.FETCH_APP_BUILDER_NOTIFICATION_SOURCES_SUCCESS: {
      const notificationSources = (action as FetchNotificationSourcesResponse).response as NotificationSource[]
      return {
        ...state,
        notificationSources: notificationSources.sort((a, b) => a.title.localeCompare(b.title))
      }
    }
    case actionTypes.DEPLOY_APP_BUILDER_FEATURE_SUCCESS: {
      return {
        ...state,
        // @ts-expect-error
        feature: {
          ...state.feature,
          status: FeatureStatus.ACTIVE
        }
      }
    }
    default: {
      return state
    }
  }
}
